import { useState, useMemo } from 'react';
import { useLeads } from '@/hooks/useLeads';
import { useClientPagination } from '@/hooks/useClientPagination';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Plus, Mail, Phone, Building, DollarSign, Edit, Trash2 } from 'lucide-react';
import { LeadForm } from '@/components/leads/LeadForm';
import { DeleteConfirmDialog } from '@/components/ui/delete-confirm-dialog';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingState } from '@/components/shared/LoadingState';
import { PaginationControls } from '@/components/ui/pagination-controls';
import type { Lead } from '@/hooks/useLeads';

const statusVariants = {
  new: 'default',
  contacted: 'secondary', 
  qualified: 'outline',
  proposal: 'outline',
  won: 'default',
  lost: 'destructive'
} as const;

const statusColors = {
  new: 'bg-blue-100 text-blue-800',
  contacted: 'bg-yellow-100 text-yellow-800',
  qualified: 'bg-purple-100 text-purple-800', 
  proposal: 'bg-orange-100 text-orange-800',
  won: 'bg-green-100 text-green-800',
  lost: 'bg-red-100 text-red-800'
};

export default function Leads() {
  const { leads, isLoading, deleteLead } = useLeads();
  const [showForm, setShowForm] = useState(false);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [deletingLead, setDeletingLead] = useState<Lead | null>(null);

  // Client-side pagination
  const {
    currentPage,
    pageSize,
    totalPages,
    paginatedItems: paginatedLeads,
    setCurrentPage,
    setPageSize,
    hasNextPage,
    hasPreviousPage,
  } = useClientPagination({ items: leads });

  // Calculate stats
  const stats = useMemo(() => ({
    total: leads.length,
    new: leads.filter(l => l.status === 'new').length,
    qualified: leads.filter(l => l.status === 'qualified').length,
    potentialValue: leads.reduce((sum, lead) => sum + (lead.estimated_value || 0), 0),
  }), [leads]);

  const handleDelete = async (lead: Lead) => {
    await deleteLead(lead.id);
    setDeletingLead(null);
  };

  const handleFormSubmit = () => {
    setShowForm(false);
    setEditingLead(null);
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <LoadingState message="Loading leads..." />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Leads</h1>
            <p className="text-muted-foreground">
              Manage your construction leads and prospects
            </p>
          </div>
          <Button onClick={() => setShowForm(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Lead
          </Button>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Leads</CardTitle>
              <Building className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">New Leads</CardTitle>
              <Plus className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.new}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Qualified</CardTitle>
              <Badge className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.qualified}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Potential Value</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                ${stats.potentialValue.toLocaleString()}
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>All Leads</CardTitle>
            <CardDescription>
              A comprehensive list of all your leads
            </CardDescription>
          </CardHeader>
          <CardContent>
            {leads.length === 0 ? (
              <EmptyState
                icon={Building}
                title="No leads yet"
                description="Get started by adding your first lead"
                actionLabel="Add Your First Lead"
                onAction={() => setShowForm(true)}
              />
            ) : (
              <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Value</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedLeads.map((lead) => (
                    <TableRow key={lead.id}>
                      <TableCell className="font-medium">{lead.name}</TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {lead.email && (
                            <div className="flex items-center text-sm text-muted-foreground">
                              <Mail className="h-3 w-3 mr-1" />
                              {lead.email}
                            </div>
                          )}
                          {lead.phone && (
                            <div className="flex items-center text-sm text-muted-foreground">
                              <Phone className="h-3 w-3 mr-1" />
                              {lead.phone}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{lead.company || '-'}</TableCell>
                      <TableCell>
                        <Badge 
                          variant={statusVariants[lead.status as keyof typeof statusVariants] || 'default'}
                          className={statusColors[lead.status as keyof typeof statusColors]}
                        >
                          {lead.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {lead.estimated_value ? `$${lead.estimated_value.toLocaleString()}` : '-'}
                      </TableCell>
                      <TableCell>{lead.source || '-'}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditingLead(lead);
                              setShowForm(true);
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeletingLead(lead)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {leads.length > 0 && (
                <PaginationControls
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalCount={leads.length}
                  pageSize={pageSize}
                  onPageChange={setCurrentPage}
                  onPageSizeChange={setPageSize}
                  hasNextPage={hasNextPage}
                  hasPreviousPage={hasPreviousPage}
                />
              )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <LeadForm
        open={showForm}
        onOpenChange={setShowForm}
        lead={editingLead}
        onSubmit={handleFormSubmit}
      />

      <DeleteConfirmDialog
        open={!!deletingLead}
        onOpenChange={() => setDeletingLead(null)}
        title="Delete Lead"
        description={`Are you sure you want to delete "${deletingLead?.name}"? This action cannot be undone.`}
        onConfirm={() => deletingLead && handleDelete(deletingLead)}
      />
    </DashboardLayout>
  );
}